/**
 * Trivia Game Engine
 *
 * Core game logic for the Trivia/Quiz game.
 * Manages questions, scoring, rounds, streaks, and game flow.
 */

import {
  TriviaGameState,
  TriviaQuestion,
  TriviaPlayer,
  TriviaAnswer,
  TriviaRound,
  TriviaCategory,
  TriviaPhase,
  TriviaGameSettings,
  ALL_CATEGORIES,
  DEFAULT_QUESTIONS_PER_ROUND,
  DEFAULT_TIME_PER_QUESTION,
  MAX_POINTS_PER_QUESTION,
  MIN_POINTS_PER_QUESTION,
  STREAK_BONUS_MULTIPLIER,
} from './types';
import { questionBank } from './questionBank';

export class TriviaEngine {
  private state: TriviaGameState;
  private usedQuestionIds: Set<string> = new Set();
  private allQuestions: TriviaQuestion[];

  constructor(externalQuestions?: TriviaQuestion[]) {
    this.allQuestions = externalQuestions && externalQuestions.length > 0
      ? externalQuestions
      : questionBank;
    this.state = this.createInitialState();
  }

  /**
   * Update the question pool (e.g., after async loading completes)
   */
  setQuestionPool(questions: TriviaQuestion[]): void {
    if (questions.length > 0) {
      this.allQuestions = questions;
    }
  }

  // -------------------------------------------------------------------------
  // State Management
  // -------------------------------------------------------------------------

  getState(): TriviaGameState {
    return { ...this.state };
  }

  private createInitialState(): TriviaGameState {
    return {
      gameId: `trivia_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      phase: 'idle',
      players: [],
      currentRound: null,
      currentQuestion: null,
      questionStartTime: null,
      timeRemaining: DEFAULT_TIME_PER_QUESTION,
      questionsPerRound: DEFAULT_QUESTIONS_PER_ROUND,
      timePerQuestion: DEFAULT_TIME_PER_QUESTION,
      maxPointsPerQuestion: MAX_POINTS_PER_QUESTION,
      selectedCategories: [...ALL_CATEGORIES],
      roundsPlayed: 0,
      isGameOver: false,
    };
  }

  // -------------------------------------------------------------------------
  // Game Setup
  // -------------------------------------------------------------------------

  /**
   * Initialize a new game with the given settings and players
   */
  initializeGame(settings: TriviaGameSettings, players: TriviaPlayer[]): TriviaGameState {
    this.usedQuestionIds.clear();
    this.state = {
      ...this.createInitialState(),
      players: players.map(p => ({
        ...p,
        score: 0,
        streak: 0,
        correctAnswers: 0,
        totalAnswers: 0,
        averageResponseTime: 0,
      })),
      selectedCategories: settings.categories || [...ALL_CATEGORIES],
      questionsPerRound: settings.questionsPerRound || DEFAULT_QUESTIONS_PER_ROUND,
      timePerQuestion: settings.timePerQuestion || DEFAULT_TIME_PER_QUESTION,
      phase: 'idle',
    };
    return this.getState();
  }

  /**
   * Start a new round of questions
   */
  startRound(): TriviaGameState {
    const questions = this.selectQuestions(
      this.state.questionsPerRound,
      this.state.selectedCategories
    );

    const round: TriviaRound = {
      roundNumber: this.state.roundsPlayed + 1,
      questions,
      answers: [],
      currentQuestionIndex: 0,
      startedAt: Date.now(),
    };

    this.state = {
      ...this.state,
      currentRound: round,
      phase: 'question',
      currentQuestion: questions[0] || null,
      questionStartTime: Date.now(),
      timeRemaining: this.state.timePerQuestion,
    };

    return this.getState();
  }

  // -------------------------------------------------------------------------
  // Question Selection
  // -------------------------------------------------------------------------

  /**
   * Select random questions from the bank, avoiding repeats
   */
  private selectQuestions(count: number, categories: TriviaCategory[]): TriviaQuestion[] {
    const available = this.allQuestions.filter(
      q => categories.includes(q.category) && !this.usedQuestionIds.has(q.id)
    );

    // Shuffle and pick
    const shuffled = this.shuffleArray([...available]);
    const selected = shuffled.slice(0, count);

    // Mark as used
    selected.forEach(q => this.usedQuestionIds.add(q.id));

    // If we do not have enough, reset used pool
    if (selected.length < count) {
      this.usedQuestionIds.clear();
      const remaining = count - selected.length;
      const moreAvailable = this.allQuestions.filter(
        q => categories.includes(q.category) && !selected.find(s => s.id === q.id)
      );
      const moreShuffled = this.shuffleArray([...moreAvailable]);
      const more = moreShuffled.slice(0, remaining);
      more.forEach(q => this.usedQuestionIds.add(q.id));
      return [...selected, ...more];
    }

    return selected;
  }

  private shuffleArray<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  // -------------------------------------------------------------------------
  // Answering Questions
  // -------------------------------------------------------------------------

  /**
   * Submit an answer for the current question
   */
  submitAnswer(playerId: string, selectedOption: string): TriviaAnswer | null {
    if (
      this.state.phase !== 'question' ||
      !this.state.currentQuestion ||
      !this.state.questionStartTime
    ) {
      return null;
    }

    // Prevent duplicate answers for the same question
    const round = this.state.currentRound;
    if (!round) return null;

    const alreadyAnswered = round.answers.find(
      a => a.playerId === playerId && a.questionId === this.state.currentQuestion!.id
    );
    if (alreadyAnswered) return null;

    const responseTime = Date.now() - this.state.questionStartTime;
    const isCorrect = selectedOption === this.state.currentQuestion.correctAnswer;
    const pointsEarned = this.calculatePoints(isCorrect, responseTime, playerId);

    const answer: TriviaAnswer = {
      playerId,
      questionId: this.state.currentQuestion.id,
      selectedOption,
      isCorrect,
      responseTime,
      pointsEarned,
      timestamp: Date.now(),
    };

    // Update round answers
    round.answers.push(answer);

    // Update player stats
    this.state.players = this.state.players.map(p => {
      if (p.id !== playerId) return p;
      const newTotalAnswers = p.totalAnswers + 1;
      const newCorrectAnswers = p.correctAnswers + (isCorrect ? 1 : 0);
      const newStreak = isCorrect ? p.streak + 1 : 0;
      const totalResponseTime = p.averageResponseTime * p.totalAnswers + responseTime;
      return {
        ...p,
        score: p.score + pointsEarned,
        streak: newStreak,
        correctAnswers: newCorrectAnswers,
        totalAnswers: newTotalAnswers,
        averageResponseTime: totalResponseTime / newTotalAnswers,
      };
    });

    return answer;
  }

  /**
   * Calculate points based on correctness, speed, and streak
   */
  calculatePoints(isCorrect: boolean, responseTime: number, playerId: string): number {
    if (!isCorrect) return 0;

    const timeLimitMs = this.state.timePerQuestion * 1000;
    const timeRatio = Math.max(0, 1 - responseTime / timeLimitMs);

    // Base points: linear decay from MAX to MIN based on time
    const basePoints =
      MIN_POINTS_PER_QUESTION +
      (MAX_POINTS_PER_QUESTION - MIN_POINTS_PER_QUESTION) * timeRatio;

    // Streak bonus
    const player = this.state.players.find(p => p.id === playerId);
    const streakLevel = player ? player.streak : 0;
    const streakBonus = basePoints * streakLevel * STREAK_BONUS_MULTIPLIER;

    return Math.round(basePoints + streakBonus);
  }

  // -------------------------------------------------------------------------
  // Game Flow
  // -------------------------------------------------------------------------

  /**
   * Move to the answer reveal phase
   */
  revealAnswer(): TriviaGameState {
    if (this.state.phase !== 'question') return this.getState();
    this.state.phase = 'answer-reveal';
    return this.getState();
  }

  /**
   * Handle time expiry for the current question
   */
  handleTimeUp(): TriviaGameState {
    // Players who have not answered get 0 points
    if (this.state.currentQuestion && this.state.currentRound) {
      const answeredPlayerIds = new Set(
        this.state.currentRound.answers
          .filter(a => a.questionId === this.state.currentQuestion!.id)
          .map(a => a.playerId)
      );

      this.state.players.forEach(player => {
        if (!answeredPlayerIds.has(player.id)) {
          const timeoutAnswer: TriviaAnswer = {
            playerId: player.id,
            questionId: this.state.currentQuestion!.id,
            selectedOption: '',
            isCorrect: false,
            responseTime: this.state.timePerQuestion * 1000,
            pointsEarned: 0,
            timestamp: Date.now(),
          };
          this.state.currentRound!.answers.push(timeoutAnswer);

          // Reset streak
          this.state.players = this.state.players.map(p =>
            p.id === player.id
              ? { ...p, streak: 0, totalAnswers: p.totalAnswers + 1 }
              : p
          );
        }
      });
    }

    this.state.phase = 'answer-reveal';
    return this.getState();
  }

  /**
   * Advance to the next question or end the round
   */
  nextQuestion(): TriviaGameState {
    if (!this.state.currentRound) return this.getState();

    const nextIndex = this.state.currentRound.currentQuestionIndex + 1;

    if (nextIndex >= this.state.currentRound.questions.length) {
      // Round complete
      return this.endRound();
    }

    this.state.currentRound.currentQuestionIndex = nextIndex;
    this.state.currentQuestion = this.state.currentRound.questions[nextIndex];
    this.state.questionStartTime = Date.now();
    this.state.timeRemaining = this.state.timePerQuestion;
    this.state.phase = 'question';

    return this.getState();
  }

  /**
   * End the current round
   */
  private endRound(): TriviaGameState {
    this.state.roundsPlayed += 1;
    this.state.phase = 'game-over';
    this.state.isGameOver = true;
    this.state.currentQuestion = null;
    this.state.questionStartTime = null;
    return this.getState();
  }

  // -------------------------------------------------------------------------
  // Utility Methods
  // -------------------------------------------------------------------------

  /**
   * Get the winner of the game
   */
  getWinner(): TriviaPlayer | null {
    if (!this.state.isGameOver || this.state.players.length === 0) return null;
    const sorted = [...this.state.players].sort((a, b) => b.score - a.score);
    if (sorted.length > 1 && sorted[0].score === sorted[1].score) return null; // Draw
    return sorted[0];
  }

  /**
   * Check if the game is a draw
   */
  isDraw(): boolean {
    if (!this.state.isGameOver || this.state.players.length < 2) return false;
    const sorted = [...this.state.players].sort((a, b) => b.score - a.score);
    return sorted[0].score === sorted[1].score;
  }

  /**
   * Get current question number (1-based)
   */
  getCurrentQuestionNumber(): number {
    if (!this.state.currentRound) return 0;
    return this.state.currentRound.currentQuestionIndex + 1;
  }

  /**
   * Get total questions in current round
   */
  getTotalQuestions(): number {
    if (!this.state.currentRound) return this.state.questionsPerRound;
    return this.state.currentRound.questions.length;
  }

  /**
   * Reset the engine for a new game
   */
  reset(): void {
    this.state = this.createInitialState();
    this.usedQuestionIds.clear();
  }

  /**
   * Check if all players have answered the current question
   */
  allPlayersAnswered(): boolean {
    if (!this.state.currentRound || !this.state.currentQuestion) return false;
    const answeredCount = this.state.currentRound.answers.filter(
      a => a.questionId === this.state.currentQuestion!.id
    ).length;
    return answeredCount >= this.state.players.length;
  }
}
